/**
 * CAOS-538 — the administrative surface uses the assistant it already has.
 *
 * Setup referenced the assistant nowhere. The assistant is a platform seam every surface can
 * reach, and the part of the product that is hardest to learn was the part with no help in it.
 *
 * ── WHAT THIS RENDERS, AND WHY IT IS NOT A LIST OF PROMPTS ───────────────────────────────────
 * The shell's assistant panel offers three hardcoded suggestions ("Summarize my open work" and so
 * on). They are the same everywhere and know nothing about Setup, which is precisely the habit the
 * audit behind this ticket records: we model a prompt as a first-class type and hard-code the
 * assistant's suggestions anyway.
 *
 * So this reads what the assistant can ACTUALLY do from the kernel — `ai/config-get` returns the
 * action catalog — and renders that. Each row carries `where` a person would do the same thing by
 * hand, which comes from the same catalog entry that performs it, so the answer to "where is this
 * configured?" and the thing that does it cannot drift apart into two lists.
 *
 * ── THE HONEST EMPTY STATE ───────────────────────────────────────────────────────────────────
 * `actionsReady` is why this file does not simply render `actions.length` rows. A deployment that
 * wired no route table reports an empty list, and an empty list drawn plainly reads as "this
 * assistant can do nothing" — indistinguishable from a deployment that is misconfigured. The two
 * are different facts and are rendered differently.
 *
 * ── WHAT THIS FILE DOES NOT DECIDE ───────────────────────────────────────────────────────────
 * Permissions. It does not check them, hide rows by them, or pre-judge them. `ai/act` resolves an
 * action to the route it runs and checks the caller against THAT route's permission; a refusal
 * comes back naming the permission and is shown as given. A surface that hid what it guessed you
 * could not do would be a second, quieter authorization model that could disagree with the real
 * one — and the one it would be wrong about is the person whose grants changed a moment ago.
 */

/** The control-plane fetch seam, same shape the page surface uses. */
export type ControlPlaneFetch = (
  path: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<{ ok: boolean; json(): Promise<unknown> }>;

const defaultControlPlaneFetch: ControlPlaneFetch = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...(init ?? {}) });

/** One input an action takes, named as the kernel's route spells it. */
export interface AssistantArgumentView {
  readonly name: string;
  readonly label: string;
  readonly required: boolean;
}

/** One thing the assistant offers to do, as the kernel describes it. */
export interface AssistantActionView {
  readonly key: string;
  readonly summary: string;
  readonly where: string;
  /** The permission the change will cost. Reported, never enforced here. */
  readonly permission: string | null;
  /** What the action needs before it can run. Empty means a single button is enough. */
  readonly arguments: readonly AssistantArgumentView[];
}

export interface AssistantCapabilities {
  readonly actions: readonly AssistantActionView[];
  /** False when this deployment cannot run actions at all — see the header. */
  readonly actionsReady: boolean;
  readonly chatReady: boolean;
  readonly chatBlockedReason: string | null;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * Read what the assistant can do in this org.
 *
 * Returns `null` when the answer cannot be established — a transport failure, or a response that
 * is not the shape this expects. `null` is not an empty capability set: the caller renders "could
 * not be read" rather than "can do nothing", because reporting a failed read as a capability fact
 * is how a surface comes to state something it never learned.
 */
export async function readAssistantCapabilities(
  doFetch: ControlPlaneFetch = defaultControlPlaneFetch,
): Promise<AssistantCapabilities | null> {
  let payload: unknown;
  try {
    const res = await doFetch('/api/ai/config-get');
    payload = await res.json();
  } catch {
    return null;
  }
  const env = payload as { ok?: unknown; data?: unknown } | null;
  if (!env || env.ok !== true || typeof env.data !== 'object' || env.data === null) return null;
  const d = env.data as Record<string, unknown>;
  if (!Array.isArray(d['actions'])) return null;

  const actions: AssistantActionView[] = [];
  for (const raw of d['actions'] as unknown[]) {
    if (typeof raw !== 'object' || raw === null) continue;
    const r = raw as Record<string, unknown>;
    const key = asString(r['key']);
    if (!key) continue;
    const args: AssistantArgumentView[] = [];
    if (Array.isArray(r['arguments'])) {
      for (const rawArg of r['arguments'] as unknown[]) {
        if (typeof rawArg !== 'object' || rawArg === null) continue;
        const g = rawArg as Record<string, unknown>;
        const name = asString(g['name']);
        if (!name) continue;
        // An argument whose `required` the kernel did not state is treated as REQUIRED. Guessing
        // the other way would draw an optional-looking input for something the route refuses
        // without, and the person would find out by failing.
        args.push({ name, label: asString(g['label']) || name, required: g['required'] !== false });
      }
    }
    actions.push({
      key,
      summary: asString(r['summary']),
      where: asString(r['where']),
      permission: typeof r['permission'] === 'string' ? r['permission'] : null,
      arguments: args,
    });
  }
  return {
    actions,
    actionsReady: d['actionsReady'] === true,
    chatReady: d['chatReady'] === true,
    chatBlockedReason: typeof d['chatBlockedReason'] === 'string' ? d['chatBlockedReason'] : null,
  };
}

export interface AssistantActionOutcome {
  readonly ok: boolean;
  /** What to tell the person. Always set — a failure with nothing to say is not a result. */
  readonly message: string;
}

/**
 * Ask the assistant to perform one action.
 *
 * The refusal path matters more than the success path here. A caller without the permission gets
 * `permission.denied` naming the permission, and that is shown as given rather than flattened into
 * "something went wrong": the person needs to know which grant they are missing, and it is the
 * same envelope they would get from clicking the button on the screen itself.
 */
export async function runAssistantAction(
  key: string,
  args: Record<string, unknown>,
  doFetch: ControlPlaneFetch = defaultControlPlaneFetch,
): Promise<AssistantActionOutcome> {
  let payload: unknown;
  try {
    const res = await doFetch('/api/ai/act', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action: key, arguments: args }),
    });
    payload = await res.json();
  } catch {
    return { ok: false, message: 'The assistant could not be reached. Nothing was changed.' };
  }
  const env = payload as { ok?: unknown; data?: unknown; error?: unknown } | null;
  if (env && env.ok === true) return { ok: true, message: 'Done.' };

  const err = (env?.error ?? null) as { code?: unknown; details?: unknown; message?: unknown } | null;
  const details = (err?.details ?? null) as { permission?: unknown } | null;
  if (asString(err?.code) === 'permission.denied' || typeof details?.permission === 'string') {
    const perm = asString(details?.permission);
    return {
      ok: false,
      message: perm
        ? `You do not have permission to do that. It needs ${perm}. Nothing was changed.`
        : 'You do not have permission to do that. Nothing was changed.',
    };
  }
  const msg = asString(err?.message);
  return { ok: false, message: msg ? `That did not run: ${msg}` : 'That did not run. Nothing was changed.' };
}

export interface SetupAssistantOptions {
  /**
   * Called when somebody asks for an action, with the values they typed.
   *
   * Injected so the panel is testable without a network. The arguments are collected here rather
   * than sent empty: a button that always posts `{}` is a control that can never succeed for any
   * action whose route requires a field, and reporting that route's refusal back to the person
   * would present our own omission as their mistake.
   */
  readonly run?: (key: string, args: Record<string, string>) => void;
}

/**
 * The Setup assistant panel.
 *
 * Three states, deliberately distinct, because collapsing them is how a surface lies:
 *   • capabilities could not be read  → say so; claim nothing about what is possible
 *   • read, but this deployment runs no actions → say that, and still show what it can answer
 *   • read and ready → the catalog, each row with where it lives by hand
 */
export function renderSetupAssistant(
  caps: AssistantCapabilities | null,
  options: SetupAssistantOptions = {},
): HTMLElement {
  const root = document.createElement('section');
  root.className = 'setup-assistant';
  root.dataset.state = caps === null ? 'unread' : caps.actionsReady ? 'ready' : 'unavailable';

  const heading = document.createElement('h2');
  heading.className = 'setup-assistant__title';
  heading.textContent = 'Ask the assistant';
  root.append(heading);

  const note = document.createElement('p');
  note.className = 'setup-assistant__note';

  if (caps === null) {
    note.textContent = 'What the assistant can do here could not be read just now.';
    root.append(note);
    return root;
  }

  if (!caps.actionsReady) {
    // Not the same as "no actions", and not rendered as though it were.
    note.textContent =
      'This deployment cannot run assistant actions yet. The assistant can still answer questions.';
    root.append(note);
    return root;
  }

  note.textContent = 'It can answer where something is configured, and it can make the change for you.';
  root.append(note);

  const list = document.createElement('ul');
  list.className = 'setup-assistant__actions';
  for (const a of caps.actions) {
    const li = document.createElement('li');
    li.className = 'setup-assistant__action';
    li.dataset.action = a.key;

    const what = document.createElement('span');
    what.className = 'setup-assistant__action-summary';
    what.textContent = a.summary;

    // The answer half, carried on the same row as the doing half.
    const where = document.createElement('span');
    where.className = 'setup-assistant__action-where';
    where.textContent = `By hand: ${a.where}`;

    // One input per argument the action declares. The names come from the kernel, which pins them
    // against the target route's own accepted-field list, so what is drawn here is what the route
    // will read.
    const fields = document.createElement('div');
    fields.className = 'setup-assistant__action-fields';
    const inputs = new Map<string, HTMLInputElement>();
    for (const g of a.arguments) {
      const field = document.createElement('label');
      field.className = 'setup-assistant__action-field';
      field.textContent = g.required ? g.label : `${g.label} (optional)`;

      const input = document.createElement('input');
      input.type = 'text';
      input.name = g.name;
      input.className = 'setup-assistant__action-input';
      input.required = g.required;
      field.append(input);
      fields.append(field);
      inputs.set(g.name, input);
    }

    const problem = document.createElement('p');
    problem.className = 'setup-assistant__action-problem';
    problem.hidden = true;

    const run = document.createElement('button');
    run.type = 'button';
    run.className = 'setup-assistant__action-run';
    run.textContent = 'Ask the assistant to do this';
    run.addEventListener('click', () => {
      const values: Record<string, string> = {};
      const missing: string[] = [];
      for (const g of a.arguments) {
        const typed = (inputs.get(g.name)?.value ?? '').trim();
        if (typed) {
          values[g.name] = typed;
        } else if (g.required) {
          missing.push(g.label);
        }
        // A blank OPTIONAL argument is omitted rather than sent empty — `ai/config-set` takes a
        // partial update, so sending '' would clear a setting the person never touched.
      }
      if (missing.length > 0) {
        // Said here, before anything is sent. The route would refuse this too, but a refusal that
        // travelled to the kernel and back reads as a failure of the action rather than a field
        // nobody filled in.
        problem.textContent = `Fill in ${missing.join(' and ')} first.`;
        problem.hidden = false;
        return;
      }
      problem.hidden = true;
      options.run?.(a.key, values);
    });

    li.append(what, where);
    if (a.arguments.length > 0) li.append(fields);
    li.append(problem, run);
    list.append(li);
  }
  root.append(list);
  return root;
}
